import {
  importProvidersFrom,
  ApplicationConfig,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { UtcDateInterceptor } from './interceptors/utc-date.interceptor';

import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch
} from '@angular/common/http';

import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';

import {
  IPublicClientApplication,
  PublicClientApplication,
  Configuration,
  LogLevel,
  BrowserCacheLocation,
  InteractionType,
  InteractionStatus,
  EventType
} from '@azure/msal-browser';

import { filter, firstValueFrom } from 'rxjs';
import { PermissionService } from './services/permission.service';

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.msalConfig.auth.clientId,
    authority: environment.msalConfig.auth.authority,
    knownAuthorities: [environment.msalConfig.auth.authorityDomain],
    redirectUri: '/',
    postLogoutRedirectUri: '/',
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii) {
          console.log(message);
        }
      },
      logLevel: LogLevel.Error, // reduce noise in production; change to Info for debugging
    },
  },
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

/**
 * APP_INITIALIZER factory.
 *
 * Waits for MSAL to finish its interaction (handleRedirectObservable resolves,
 * inProgress$ reaches None) and then calls permissionService.loadPermissions()
 * so that the permissions are available before any component renders.
 *
 * If the user is not authenticated yet, permissions are left empty and the
 * authGuard / PermissionGuard will redirect to login when the user navigates.
 */
function initializePermissions(
  msalService: MsalService,
  msalBroadcastService: MsalBroadcastService,
  permissionService: PermissionService
): () => Promise<void> {
  return async () => {
    await firstValueFrom(msalService.initialize());

    // Process any pending redirect (B2C login redirect callback)
    try {
      const result = await firstValueFrom(msalService.handleRedirectObservable());
      if (result?.account) {
        msalService.instance.setActiveAccount(result.account);
      }
    } catch (err) {
      console.warn('[App Init] handleRedirectObservable failed:', err);
    }

    // Wait for any in-progress interaction to settle
    await firstValueFrom(
      msalBroadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None)
      )
    );

    // Set active account if not already set
    if (!msalService.instance.getActiveAccount()) {
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        msalService.instance.setActiveAccount(accounts[0]);
      }
    }

    // Load permissions if the user is authenticated
    if (msalService.instance.getActiveAccount()) {
      await permissionService.loadPermissions();
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(BrowserModule),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UtcDateInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: (
        msalSvc: MsalService,
        broadcastSvc: MsalBroadcastService,
        permSvc: PermissionService
      ) => initializePermissions(msalSvc, broadcastSvc, permSvc),
      deps: [MsalService, MsalBroadcastService, PermissionService],
      multi: true,
    },
  ],
};
